/**
 * Audio Waveform Utilities
 *
 * Extracts and manages waveform data for audio visualization.
 * Uses Web Audio API to decode audio and sample amplitude data.
 */

export interface WaveformData {
  /** Sampled amplitude values (0-255, where 128 is silence) */
  samples: Uint8Array;
  /** Number of samples per second of audio */
  samplesPerSecond: number;
  /** Duration of the audio in seconds */
  duration: number;
}

/**
 * Convert Uint8Array to base64 string (works with binary data)
 */
export function arrayBufferToBase64(buffer: Uint8Array): string {
  let binary = '';
  const len = buffer.length;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to Uint8Array
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert data URL to ArrayBuffer
 */
function dataUrlToArrayBuffer(dataUrl: string): ArrayBuffer {
  const base64 = dataUrl.split(',')[1];
  if (!base64) throw new Error('Invalid data URL');
  
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Extract waveform data from an audio file
 * @param audioData - Base64 data URL or blob URL of the audio
 * @param sampleCount - Number of samples to extract (default: 150)
 * @returns WaveformData or null if extraction fails
 */
export async function extractWaveform(
  audioData: string,
  sampleCount: number = 150
): Promise<WaveformData | null> {
  try {
    console.log('[Waveform] Starting extraction, data length:', audioData.length);
    
    // Create audio context
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    
    // Get ArrayBuffer from data URL or blob URL
    let arrayBuffer: ArrayBuffer;
    if (audioData.startsWith('data:')) {
      // Handle data URL directly
      arrayBuffer = dataUrlToArrayBuffer(audioData);
      console.log('[Waveform] Decoded data URL, buffer size:', arrayBuffer.byteLength);
    } else {
      // Fetch blob URL
      const response = await fetch(audioData);
      arrayBuffer = await response.arrayBuffer();
      console.log('[Waveform] Fetched blob URL, buffer size:', arrayBuffer.byteLength);
    }
    
    console.log('[Waveform] Decoding audio data...');
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
    console.log('[Waveform] Audio decoded, duration:', audioBuffer.duration, 'channels:', audioBuffer.numberOfChannels);
    
    // Get the first channel's data
    const channelData = audioBuffer.getChannelData(0);
    const duration = audioBuffer.duration;
    
    // Calculate samples
    const samples = new Uint8Array(sampleCount);
    const samplesPerStep = Math.floor(channelData.length / sampleCount);
    
    for (let i = 0; i < sampleCount; i++) {
      const start = i * samplesPerStep;
      const end = Math.min(start + samplesPerStep, channelData.length);
      
      // Calculate peak amplitude in this slice
      let peak = 0;
      for (let j = start; j < end; j++) {
        const amplitude = Math.abs(channelData[j]);
        if (amplitude > peak) {
          peak = amplitude;
        }
      }
      
      // Convert to 0-255 range (128 is silence)
      samples[i] = Math.round(128 + (peak * 127));
    }
    
    // Close the audio context to free resources
    await audioContext.close();
    
    console.log('[Waveform] Extraction complete, samples:', sampleCount);
    
    return {
      samples,
      samplesPerSecond: sampleCount / duration,
      duration
    };
  } catch (error) {
    console.error('[Waveform] Failed to extract waveform:', error);
    return null;
  }
}

/**
 * Get samples for a specific time range within the waveform
 * @param waveform - The waveform data
 * @param startTime - Start time in seconds
 * @param endTime - End time in seconds
 * @returns Subset of samples for the time range
 */
export function getWaveformSlice(
  waveform: WaveformData,
  startTime: number,
  endTime: number
): Uint8Array {
  const startSample = Math.floor(startTime * waveform.samplesPerSecond);
  const endSample = Math.floor(endTime * waveform.samplesPerSecond);
  
  return waveform.samples.slice(
    Math.max(0, startSample),
    Math.min(waveform.samples.length, endSample)
  );
}

/**
 * Draw waveform on a canvas context
 * @param ctx - Canvas 2D context
 * * @param waveform - Waveform data
 * @param width - Canvas width
 * @param height - Canvas height
 * @param trimStart - Trim start in seconds (for visual indication)
 * @param trimEnd - Trim end in seconds (for visual indication)
 * @param color - Color for the waveform (default: white)
 */
export function drawWaveform(
  ctx: CanvasRenderingContext2D,
  waveform: WaveformData,
  width: number,
  height: number,
  trimStart: number = 0,
  trimEnd: number = 0,
  color: string = 'rgba(255, 255, 255, 0.8)',
  trimColor: string = 'rgba(255, 255, 255, 0.3)'
): void {
  const samples = waveform.samples;
  const sampleWidth = width / samples.length;
  const centerY = height / 2;
  
  ctx.clearRect(0, 0, width, height);
  
  // Calculate trim boundaries in sample indices
  const trimStartSample = Math.floor(trimStart * waveform.samplesPerSecond);
  const trimEndSample = samples.length - Math.floor(trimEnd * waveform.samplesPerSecond);
  
  // Draw each sample as a vertical bar
  for (let i = 0; i < samples.length; i++) {
    const sample = samples[i];
    const amplitude = Math.abs(sample - 128) / 128; // 0 to 1
    const barHeight = amplitude * height * 0.9; // Leave some padding
    
    const x = i * sampleWidth;
    const barWidth = Math.max(1, sampleWidth - 0.5); // Small gap between bars
    
    // Determine color based on trim
    const isTrimmed = i < trimStartSample || i >= trimEndSample;
    ctx.fillStyle = isTrimmed ? trimColor : color;
    
    // Draw the bar centered vertically
    ctx.fillRect(
      x,
      centerY - barHeight / 2,
      barWidth,
      barHeight
    );
  }
}
